import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Play } from 'lucide-react';
import { heroSlides } from '../mock';
import { Button } from './ui/button';

export default function Hero() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % heroSlides.length), 6500);
    return () => clearInterval(t);
  }, []);

  const slide = heroSlides[i];
  const go = (dir) => setI((v) => (v + dir + heroSlides.length) % heroSlides.length);

  return (
    <section id="home" className="relative min-h-screen w-full overflow-hidden bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Decorative bubbles */}
      <div className="bubble w-40 h-40 top-24 -left-10 opacity-70" />
      <div className="bubble w-24 h-24 top-1/3 right-16 opacity-60" style={{ animationDelay: '1.5s' }} />
      <div className="bubble w-16 h-16 bottom-24 left-1/4 opacity-50" style={{ animationDelay: '3s' }} />
      <div className="bubble w-56 h-56 -bottom-16 -right-10 opacity-40" style={{ animationDelay: '2s' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 lg:pt-40 grid lg:grid-cols-2 gap-12 items-center">
        <div key={slide.id} className="slide-enter relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-sky-200 shadow-sm mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium tracking-wide text-slate-700">Patented Technology • Made for Hospitality & Marine</span>
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] text-slate-900">
            {slide.title} <span className="water-text">{slide.highlight}</span>
          </h1>

          <p className="mt-5 text-lg font-medium text-sky-700">{slide.subtitle}</p>
          <p className="mt-3 text-base text-slate-600 max-w-xl leading-relaxed">{slide.description}</p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button size="lg" className="btn-primary bg-gradient-to-r from-sky-500 to-cyan-600 hover:from-sky-600 hover:to-cyan-700 text-white rounded-full px-8 h-12 shadow-xl shadow-sky-200" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>
              {slide.cta} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <button className="flex items-center gap-3 group" onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}>
              <span className="w-12 h-12 rounded-full bg-white border border-sky-200 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <Play className="w-4 h-4 text-sky-600 fill-sky-600 ml-0.5" />
              </span>
              <span className="text-sm font-medium text-slate-700 group-hover:text-sky-700">Watch How It Works</span>
            </button>
          </div>

          {/* Slider controls */}
          <div className="mt-14 flex items-center gap-4">
            <button onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-sky-200 bg-white/70 hover:bg-sky-50 flex items-center justify-center transition-colors" aria-label="prev">
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <div className="flex items-center gap-2">
              {heroSlides.map((s, idx) => (
                <button key={s.id} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-10 bg-gradient-to-r from-sky-500 to-cyan-600' : 'w-4 bg-sky-200'}`} aria-label={`slide ${idx + 1}`} />
              ))}
            </div>
            <button onClick={() => go(1)} className="w-11 h-11 rounded-full border border-sky-200 bg-white/70 hover:bg-sky-50 flex items-center justify-center transition-colors" aria-label="next">
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
            <div className="ml-4 text-xs tracking-[0.25em] text-slate-500">{String(i + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}</div>
          </div>
        </div>

        {/* Visual */}
        <div key={`img-${slide.id}`} className="relative slide-enter">
          <div className="relative w-full aspect-[4/5] max-w-lg mx-auto">
            <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-br from-sky-200 to-cyan-100 blur-3xl opacity-60" />
            <div className="relative w-full h-full rounded-[3rem] overflow-hidden shadow-2xl shadow-sky-200/60 border border-white">
              <img src={slide.image} alt="upgrade water" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/30 via-transparent to-transparent" />
            </div>
            {/* Floating chip */}
            <div className="absolute -left-6 top-10 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-sky-100 animate-[floatSlow_5s_ease-in-out_infinite]">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center text-white font-bold">O₃</div>
              <div>
                <div className="text-xs text-slate-500">Ozonated</div>
                <div className="text-sm font-semibold text-slate-900">99.9% Pure</div>
              </div>
            </div>
            <div className="absolute -right-6 bottom-16 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-sky-100 animate-[floatSlow_5s_ease-in-out_infinite]" style={{ animationDelay: '1s' }}>
              <div className="text-right">
                <div className="text-xs text-slate-500">Water from air</div>
                <div className="text-sm font-semibold text-slate-900">Up to 500L/day</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center text-white text-lg">≈</div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <svg className="wave-divider absolute bottom-0 left-0" viewBox="0 0 1440 80" preserveAspectRatio="none">
        <path d="M0,40 C240,80 480,0 720,32 C960,64 1200,80 1440,40 L1440,80 L0,80 Z" fill="#ffffff" />
      </svg>
    </section>
  );
}
